import { useCallback, useSyncExternalStore } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// ---------------------------------------------------------------------------
// One shared auth store behind every useAuth() call (load-speed Phase 2b,
// docs/perf/2026-10-15-phase2b-attribution.md). useAuth used to keep its own
// state, its own onAuthStateChange subscription and its own getSession() per
// call site: ~190 of each on the first /inbox mount, because every gate hook
// reaches it several times. Each one takes the auth-js storage lock in turn,
// and each grant re-rendered the shell, so Layout ran ~90 times before the page
// could query. Now the first subscriber starts ONE listener + ONE getSession,
// and every caller reads the same snapshot. The values and their order are
// the same as before: loading until the first callback or getSession, then
// { session, user, loading: false }, updated on every auth event. When the
// last subscriber leaves, the listener is torn down and the store resets, so
// a later mount starts from loading again, exactly like a fresh hook did.
// ---------------------------------------------------------------------------

type AuthSnapshot = { user: User | null; session: Session | null; loading: boolean };

const INITIAL_SNAPSHOT: AuthSnapshot = { user: null, session: null, loading: true };

let snapshot: AuthSnapshot = INITIAL_SNAPSHOT;
const listeners = new Set<() => void>();
let stopListening: (() => void) | null = null;
// Bumped on every teardown so a getSession() that resolves after it is ignored
// (the old per-instance setState on an unmounted hook was a no-op too).
let generation = 0;

function applySession(session: Session | null): void {
  const user = session?.user ?? null;
  if (!snapshot.loading && snapshot.session === session && snapshot.user === user) return;
  snapshot = { user, session, loading: false };
  for (const listener of listeners) listener();
}

function startListening(): void {
  const current = generation;
  // Set up auth state listener FIRST
  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    if (current === generation) applySession(session);
  });
  stopListening = () => subscription.unsubscribe();

  // THEN check for existing session
  void supabase.auth.getSession().then(({ data: { session } }) => {
    if (current === generation) applySession(session);
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (!stopListening) startListening();
  return () => {
    listeners.delete(listener);
    if (listeners.size > 0 || !stopListening) return;
    stopListening();
    stopListening = null;
    generation += 1;
    snapshot = INITIAL_SNAPSHOT;
  };
}

function getSnapshot(): AuthSnapshot {
  return snapshot;
}

export const useAuth = (): { user: User | null; session: Session | null; loading: boolean; signOut: () => Promise<void>; isAuthenticated: boolean } => {
  const { user, session, loading } = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // supabase-js absorbs 401/403/404 itself (session already dead server-side)
  // and clears local storage before returning error: null — we never see those.
  // A non-null error here means the local session survived, i.e. the user is
  // still signed in. Do NOT "verify" this with getSession(): a failed token
  // refresh also returns session: null without clearing storage, which would
  // create a second false-success on top of this one.
  const signOut = useCallback(async (): Promise<void> => {
    const { error } = await supabase.auth.signOut();
    if (!error) return;
    console.error('Error signing out:', error);
    throw new Error('Sign out failed — you are still signed in. Check your connection and try again.');
  }, []);

  return {
    user,
    session,
    loading,
    signOut,
    isAuthenticated: !!user
  };
};